import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, AlertCircle, FileText, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import './DailyBriefingSummaryCard.css';

interface DailyBriefingSummary {
  briefing_id: string;
  briefing_date: string;
  generated_at: string;
  executive_summary: string;
  act_mode_count: number;
  total_active_issues: number;
  has_viewed: boolean;
  is_new: boolean;
}

const DailyBriefingSummaryCard: React.FC = () => {
  const [summary, setSummary] = useState<DailyBriefingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBriefingSummary();
  }, []);

  const fetchBriefingSummary = async () => {
    try {
      const data = await api.getDailyBriefingSummary();
      if (data.success) {
        setSummary(data.summary);
      } else {
        setError(data.message || 'Failed to load briefing');
      }
    } catch (err: any) {
      console.error('Error fetching briefing summary:', err);
      if (err.status === 404 || err.message.includes('404') || err.message.includes('not found')) {
        setError('No briefings available yet');
      } else {
        setError('Failed to load briefing');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleViewBriefing = () => {
    if (summary) {
      navigate('/daily-briefing');
    }
  };

  if (loading) {
    return (
      <div className="daily-briefing-card">
        <div className="card-header">
          <div className="header-icon">
            <FileText size={20} />
          </div>
          <h3 className="card-title">Daily Briefing</h3>
        </div>
        <div className="card-body loading-state">
          <Loader2 className="spinner" size={24} />
          <p>Loading briefing...</p>
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="daily-briefing-card">
        <div className="card-header">
          <div className="header-icon">
            <FileText size={20} />
          </div>
          <h3 className="card-title">Daily Briefing</h3>
        </div>
        <div className="card-body empty-state">
          <AlertCircle size={32} className="empty-icon" />
          <p className="empty-message">{error || 'No briefings available'}</p>
          <p className="empty-hint">Your first briefing will be generated at 6:00 AM</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`daily-briefing-card ${summary.is_new ? 'has-new-badge' : ''}`}>
      {summary.is_new && (
        <div className="new-badge">
          <span>NEW</span>
        </div>
      )}
      
      <div className="card-header">
        <div className="header-icon">
          <FileText size={20} />
        </div>
        <h3 className="card-title">Daily Briefing</h3>
        <div className="header-date">
          <Calendar size={16} />
          <span>{formatDate(summary.briefing_date)}</span>
        </div>
      </div>
      
      <div className="update-timestamp">
        Updated at {formatTime(summary.generated_at)}
      </div>

      <div className="card-body">
        {/* Executive Summary */}
        <div className="executive-summary">
          <p>{summary.executive_summary}</p>
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="stat-item">
            <div className="stat-icon act-mode">
              <AlertCircle size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{summary.act_mode_count}</div>
              <div className="stat-label">ACT Mode Issues</div>
            </div>
          </div>

          <div className="stat-item">
            <div className="stat-icon total-issues">
              <TrendingUp size={18} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{summary.total_active_issues}</div>
              <div className="stat-label">Total Active Issues</div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <button 
          className="view-full-button"
          onClick={handleViewBriefing}
        >
          <span>View Full Briefing</span>
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default DailyBriefingSummaryCard;
